import React from 'react';
import { Button, Input, Checkbox, Overlay, Text, Stack, ControlLink, LoadingBalls } from '@moda/om';
import { noop, useForm, deferUntilNewTitle } from '@moda/portal-stanchions';
import { usePasswordValidation } from '../../../hooks/usePasswordValidation';
import { useAuth } from '../../../hooks/useAuth';
import { AuthAction, AuthPayloadSuccess } from '../../../lib/authenticate';
import { tc } from '../../../lib/trackingContext';
import { Link } from '../../../components/Link';
import { hrefFor } from '../../../routers';
import { SocialAuthButtons } from '../SocialAuthButtons';
import { RegisterPasswordInput } from './RegisterPasswordInput';

type RegisterProps = {
  className: string;
  defaultValues?: Partial<RegisterFormValues>;
  onAuth: (authUser?: AuthPayloadSuccess) => void;
  showAuthModal?: () => void;
  hideAuthModal?: () => void;
  showCancelButton?: boolean;
};

export type RegisterFormValues = {
  registerEmail: string;
  registerFirstName: string;
  registerLastName: string;
  registerPassword: string;
  registerRemember: boolean;
  registerEmailList: boolean;
};

const REGISTER_TITLE = 'Not a Member?';
const REGISTER_MESSAGE =
  'Create a Moda Operandi account to check your orders, save your information and keep your favorite products.';
const REGISTER_SUBMIT = 'REGISTER NOW';

const validations = {
  registerFirstName: {
    required: {
      message: 'Please enter first name.',
      value: true
    }
  },
  registerLastName: {
    required: {
      message: 'Please enter last name.',
      value: true
    }
  },
  registerEmail: {
    email: {
      message: 'Invalid email address.',
      value: true
    },
    required: {
      message: 'Please enter an email address.',
      value: true
    }
  }
};

export const Register: React.FC<RegisterProps> = ({
  className,
  defaultValues,
  hideAuthModal,
  showAuthModal,
  onAuth,
  showCancelButton
}) => {
  const { error: errorAuth, handleAuth, loading } = useAuth(AuthAction.Register);
  const { passwordValidation } = usePasswordValidation();

  const onSubmit = (values: RegisterFormValues) =>
    handleAuth({
      email: values.registerEmail,
      first_name: values.registerFirstName,
      last_name: values.registerLastName,
      password: values.registerPassword,
      remember: values.registerRemember,
      opted_in: values.registerEmailList
    })
      .then(authUser => {
        onAuth(authUser);

        return authUser;
      })
      .then(authUser =>
        deferUntilNewTitle(() =>
          tc.track('Registration Completed', { addToTrackingEvent: { label: authUser?.id || '' } })
        )
      )
      .catch(noop);

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit,
    validations: { ...validations, registerPassword: passwordValidation }
  });

  return (
    <>
      <div className={className} data-testid="registerSection">
        <form onSubmit={handleSubmit}>
          <div className="Authentication__title">{REGISTER_TITLE}</div>
          <div className="Authentication__message">{REGISTER_MESSAGE}</div>
          <div className="Authentication__error" role="alert">
            {errorAuth?.message}
          </div>

          <div className="Authentication__fields">
            <div className="Authentication__alignedInputs">
              <Input
                className="Authentication__input"
                defaultValue={defaultValues?.registerFirstName}
                error={fieldErrors.registerFirstName?.message || errorAuth != null}
                id="registerFirstName"
                data-testid="registerFirstName"
                placeholder="First Name"
                type="text"
                maxLength={50}
              />

              <Input
                className="Authentication__input"
                defaultValue={defaultValues?.registerLastName}
                error={fieldErrors.registerLastName?.message || errorAuth != null}
                id="registerLastName"
                data-testid="registerLastName"
                placeholder="Last Name"
                type="text"
                maxLength={50}
              />
            </div>

            <Input
              className="Authentication__input"
              defaultValue={defaultValues?.registerEmail}
              error={fieldErrors.registerEmail?.message || errorAuth != null}
              id="registerEmail"
              data-testid="registerEmail"
              placeholder="Email"
              type="email"
            />

            <RegisterPasswordInput
              className="Authentication__input"
              error={fieldErrors.registerPassword?.message || errorAuth != null}
            />

            <div className="Authentication__checkboxes">
              <Checkbox
                className="Authentication__checkbox"
                defaultChecked={defaultValues?.registerRemember ?? true}
                id="registerRemember"
                data-testid="registerRemember"
                type="checkbox"
              >
                Remember me
              </Checkbox>

              <Checkbox
                className="Authentication__checkbox"
                defaultChecked={defaultValues?.registerEmailList ?? true}
                id="registerEmailList"
                data-testid="registerEmailList"
                type="checkbox"
              >
                Put me on the email list for style inspiration &amp; special access
              </Checkbox>
            </div>

            <Button className="Authentication__button" type="submit" data-testid="registerSubmit">
              {REGISTER_SUBMIT}
            </Button>

            {showCancelButton && (
              <Button
                className="Authentication__button Authentication__button--cancel-register"
                secondary
                type="button"
                onClick={hideAuthModal}
              >
                Cancel
              </Button>
            )}

            <Stack space={2} direction="horizontal">
              <Text>
                By clicking Register Now or registering an account with us, you agree to our{' '}
                <ControlLink
                  to={hrefFor.TermsAndConditionsPage()}
                  className="Authentication__terms"
                  underlined
                  target="_blank"
                >
                  Terms &amp; Conditions
                </ControlLink>
                <Text> and </Text>
                <ControlLink
                  to={hrefFor.PrivacyPolicyPage()}
                  className="Authentication__privacy"
                  underlined
                  target="_blank"
                >
                  Privacy Policy.
                </ControlLink>
              </Text>
            </Stack>
          </div>
        </form>

        <div className="Authentication__divider" />

        <SocialAuthButtons
          action={AuthAction.Register}
          showAmazonMessage
          showAuthModal={showAuthModal}
          hideAuthModal={hideAuthModal}
          onAuth={onAuth}
        />

        <div className="Authentication__divider" />

        <div className="Authentication__optOut">
          <Text>
            To opt out,{' '}
            <Link
              className="Authentication__opt-out-link "
              to={hrefFor.UnsubscribePage()}
              rel="noopener noreferrer"
              target="_blank"
            >
              unsubscribe here
            </Link>{' '}
            or in our emails.
          </Text>
        </div>
      </div>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
